import { Component, JSX, Show } from "solid-js";

export type StatsTrend = "up" | "down" | "neutral";

interface StatsCardProps {
  title: string;
  value: string | number;
  description?: string | JSX.Element;
  icon?: JSX.Element;
  trend?: StatsTrend;
  trendValue?: string;
  loading?: boolean;
  class?: string;
}

const trendColors: Record<StatsTrend, string> = {
  up: "text-success",
  down: "text-error",
  neutral: "text-base-content/50",
};

const trendIcons: Record<StatsTrend, string> = {
  up: "\u2191",
  down: "\u2193",
  neutral: "\u2192",
};

export const StatsCard: Component<StatsCardProps> = (props) => {
  return (
    <div class={`stat ${props.class ?? ""}`}>
      <Show when={props.icon}>
        <div class="stat-figure text-primary">{props.icon}</div>
      </Show>
      <div class="stat-title">{props.title}</div>
      <Show
        when={!props.loading}
        fallback={
          <div class="stat-value" role="status" aria-busy="true">
            <span class="loading loading-spinner loading-sm"></span>
          </div>
        }>
        <div class="stat-value">{props.value}</div>
      </Show>
      <Show when={props.description || props.trend}>
        <div class="stat-desc flex items-center gap-1">
          <Show when={props.trend}>
            <span class={trendColors[props.trend!]}>
              {trendIcons[props.trend!]} {props.trendValue}
            </span>
          </Show>
          <Show when={props.description}>{props.description}</Show>
        </div>
      </Show>
    </div>
  );
};

interface StatsGridProps {
  children: JSX.Element;
  class?: string;
  shadow?: boolean;
}

export const StatsGrid: Component<StatsGridProps> = (props) => {
  return (
    <div class={`stats w-full ${props.shadow !== false ? "shadow" : ""} ${props.class ?? ""}`}>
      {props.children}
    </div>
  );
};

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: JSX.Element;
  variant?: "default" | "success" | "warning" | "error" | "info";
  loading?: boolean;
  onClick?: () => void;
  class?: string;
}

const variantClasses: Record<string, { border: string; text: string }> = {
  default: { border: "border-l-primary", text: "text-base-content" },
  success: { border: "border-l-success", text: "text-success" },
  warning: { border: "border-l-warning", text: "text-warning" },
  error: { border: "border-l-error", text: "text-error" },
  info: { border: "border-l-info", text: "text-info" },
};

export const MetricCard: Component<MetricCardProps> = (props) => {
  const variant = () => props.variant ?? "default";
  const classes = () => variantClasses[variant()];

  return (
    <div
      class={`card bg-base-100 shadow-sm border-l-4 ${classes().border} ${
        props.onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""
      } ${props.class ?? ""}`}
      onClick={props.onClick}>
      <div class="card-body p-4">
        <div class="flex items-start justify-between">
          <div>
            <p class="text-sm text-base-content/70">{props.title}</p>
            <Show
              when={!props.loading}
              fallback={<span class="loading loading-spinner loading-sm" role="status" aria-busy="true"></span>}>
              <p class={`text-2xl font-bold ${classes().text}`}>{props.value}</p>
            </Show>
            <Show when={props.subtitle}>
              <p class="text-xs text-base-content/50 mt-1">{props.subtitle}</p>
            </Show>
          </div>
          <Show when={props.icon}>
            <div class="text-2xl opacity-50">{props.icon}</div>
          </Show>
        </div>
      </div>
    </div>
  );
};
